"use client"

import React, { createContext, useContext } from "react"
import { cn } from "../../../shared/lib/utils"
import DashboardAccordion from "../../../shared/components/dashboard-accordion"
import { useDirtyFields } from "../context/dirty-fields-context"

// Workscope title colors (distinct from status pills): Relief=red, Rebuilding=blue, Goodwill=green
const RELIEF_HEADINGS = new Set([
    "Tree, Exterior Debris & Heavy Equipment",
    "Roofing",
    "Crawl Space",
    "Muck & Gut",
    "Mold Remediation",
])
const REBUILDING_HEADINGS = new Set([
    "Rebuild Prep",
    "Rough Carpentry",
    "Electrical",
    "HVAC",
    "Plumbing",
    "Insulation Install",
    "Drywall Hanging",
    "Mud and Tape",
    "Block Wall",
    "Rendering/Stucco",
    "Bathroom Rebuild",
    "Cabinet Install",
    "Interior Paint",
    "Flooring Install",
    "Install Interior Doors",
    "Trim Install",
    "Exterior Door Install",
    "Driveway Repair",
    "Storm Shutters",
])
const GOODWILL_HEADINGS = new Set([
    "Miscellaneous Goodwill",
])

function getWorkscopeTitleClass(heading: string): string | undefined {
    if (RELIEF_HEADINGS.has(heading)) return "text-red-700"
    if (REBUILDING_HEADINGS.has(heading)) return "text-blue-700"
    if (GOODWILL_HEADINGS.has(heading)) return "text-green-700"
    return undefined
}

/** Context so form labels can match the card title color (relief=red, rebuilding=blue, goodwill=green) */
const SectionColorContext = createContext<string | null>(null)

// Inline colors so label colors always apply (no Tailwind dependency). Export for fallback.
export const WORKSCOPE_LABEL_COLORS = {
    relief: "#b91c1c",   // red-700
    rebuilding: "#1d4ed8", // blue-700
    goodwill: "#15803d",   // green-700
} as const

/** Hook for form labels: returns inline style so color is guaranteed. Pass false for workscope status titles (red/blue), true for Goodwill titles (green). */
export function useWorkscopeLabelStyle(isGoodwill: boolean): React.CSSProperties | undefined {
    const heading = useContext(SectionColorContext)
    if (!heading) return undefined
    if (isGoodwill) return { color: WORKSCOPE_LABEL_COLORS.goodwill }
    if (RELIEF_HEADINGS.has(heading)) return { color: WORKSCOPE_LABEL_COLORS.relief }
    if (REBUILDING_HEADINGS.has(heading)) return { color: WORKSCOPE_LABEL_COLORS.rebuilding }
    return undefined
}

/** Hook for form labels (class-based, for card title): pass false for workscope titles (same as card), true for Goodwill titles (green). */
export function useWorkscopeLabelClass(isGoodwill: boolean): string | undefined {
    const heading = useContext(SectionColorContext)
    if (!heading) return undefined
    if (isGoodwill) return "text-green-700"
    return getWorkscopeTitleClass(heading) ?? undefined
}

type HomeDetailsWrapperAccordionProps = {
    children: React.ReactNode,
    heading: string,
    wrapperClassName?: string,
    fieldNames?: string[] // Array of field names to check for dirty state
}

function HomeDetailsWrapperAccordion({ children, heading, wrapperClassName, fieldNames = [] }: HomeDetailsWrapperAccordionProps) {
    const { isFieldDirty } = useDirtyFields()
    
    // Check if any of the fields in this section are dirty
    const isDirty = fieldNames.some(fieldName => isFieldDirty(fieldName))
    const headingClassName = getWorkscopeTitleClass(heading)
    
    return (
        <SectionColorContext.Provider value={heading}>
            <DashboardAccordion heading={heading} className="bg-white" headingClassName={headingClassName}>
                <div className={cn(
                    "grid grid-cols-1 gap-5 items-end",
                    wrapperClassName || "xl:grid-cols-3"
                )}>{children}</div>
            </DashboardAccordion>
        </SectionColorContext.Provider>
    )
}

export default HomeDetailsWrapperAccordion
